import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDialogFocus, useExitTransition } from "./Dialog";
import SlidingIndicator from "./SlidingIndicator";
import {
  MessageCircle,
  Swords,
  Trash2,
  X,
  Clock,
  ChevronRight,
  CheckSquare,
  Square,
  MinusSquare,
  Loader2,
} from "lucide-react";
import {
  ChatSession,
  DebateSession,
  loadChatSessions,
  loadDebateSessions,
  deleteChatSession,
  deleteDebateSession,
  deleteChatSessionsBatch,
  deleteDebateSessionsBatch,
} from "../firebase/history";

interface HistorySidebarProps {
  uid: string;
  isOpen: boolean;
  onClose: () => void;
  onLoadChat: (session: ChatSession) => void;
  onLoadDebate: (session: DebateSession) => void;
}

type Tab = "chat" | "debate";

interface RowModel {
  id: string;
  title: string;
  updatedAt: any;
  detail: string;
  onOpen: () => void;
}

const HistorySidebar: React.FC<HistorySidebarProps> = ({
  uid,
  isOpen,
  onClose,
  onLoadChat,
  onLoadDebate,
}) => {
  const [tab, setTab] = useState<Tab>("chat");
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [debateSessions, setDebateSessions] = useState<DebateSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useDialogFocus(dialogRef, isOpen, onClose);
  const mounted = useExitTransition(isOpen);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [chats, debates] = await Promise.all([
        loadChatSessions(uid),
        loadDebateSessions(uid),
      ]);
      setChatSessions(chats);
      setDebateSessions(debates);
    } catch (err) {
      console.error("Failed to load history:", err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    if (isOpen) refresh();
    else {
      setConfirmingId(null);
      setConfirmBulk(false);
      setDeleteError(null);
    }
  }, [isOpen, refresh]);

  const switchTab = (next: Tab) => {
    setTab(next);
    setSelected(new Set());
    setConfirmingId(null);
    setConfirmBulk(false);
    setDeleteError(null);
  };

  // Deletion is permanent, so each row asks once inline before removing it.
  const handleDelete = async (id: string) => {
    setDeletingId(id);
    setDeleteError(null);
    try {
      if (tab === "chat") {
        await deleteChatSession(uid, id);
        setChatSessions((prev) => prev.filter((s) => s.id !== id));
      } else {
        await deleteDebateSession(uid, id);
        setDebateSessions((prev) => prev.filter((s) => s.id !== id));
      }
      setConfirmingId(null);
      listRef.current?.focus();
    } catch (err) {
      console.error("Failed to delete session:", err);
      setDeleteError("Couldn’t delete that item. Check your connection and try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleSelect = (id: string) => {
    setConfirmBulk(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const currentIds = (tab === "chat" ? chatSessions : debateSessions).map(
    (s) => s.id,
  );
  const allSelected =
    currentIds.length > 0 && currentIds.every((id) => selected.has(id));
  const someSelected = currentIds.some((id) => selected.has(id));

  const toggleSelectAll = () => {
    setConfirmBulk(false);
    setSelected(allSelected ? new Set() : new Set(currentIds));
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
    setConfirmBulk(false);
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    const remaining = currentIds.filter((id) => !selected.has(id)).length;
    setBulkDeleting(true);
    setDeleteError(null);
    try {
      if (tab === "chat") {
        await deleteChatSessionsBatch(uid, ids);
        setChatSessions((prev) => prev.filter((s) => !ids.includes(s.id)));
      } else {
        await deleteDebateSessionsBatch(uid, ids);
        setDebateSessions((prev) => prev.filter((s) => !ids.includes(s.id)));
      }
      setSelected(new Set());
      setConfirmBulk(false);
      if (remaining === 0) exitSelectMode();
    } catch (err) {
      console.error("Failed to delete sessions:", err);
      setDeleteError("Couldn’t delete the selected items. Nothing was removed; try again.");
    } finally {
      setBulkDeleting(false);
    }
  };

  const formatDate = (ts: any) => {
    if (!ts) return "";
    const date: Date = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86_400_000);
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (!mounted) return null;
  const closing = !isOpen;
  const noun = tab === "chat" ? "chat" : "debate";

  const rows: RowModel[] =
    tab === "chat"
      ? chatSessions.map((s) => ({
          id: s.id,
          title: s.title || "Untitled chat",
          updatedAt: s.updatedAt,
          detail: `${s.messages.length} message${s.messages.length !== 1 ? "s" : ""}`,
          onOpen: () => { onLoadChat(s); onClose(); },
        }))
      : debateSessions.map((s) => ({
          id: s.id,
          title: s.topic || "Untitled debate",
          updatedAt: s.updatedAt,
          detail:
            s.debateMode === "single"
              ? "Deep analysis"
              : `${s.experts.length} expert${s.experts.length !== 1 ? "s" : ""}`,
          onOpen: () => { onLoadDebate(s); onClose(); },
        }));

  const rowMeta = (row: RowModel) => (
    <span className="flex items-center gap-2 mt-1 text-xs text-muted">
      <span className="flex items-center gap-1">
        <Clock size={10} aria-hidden="true" />
        {formatDate(row.updatedAt)}
      </span>
      <span aria-hidden="true">·</span>
      <span className="tabular-nums">{row.detail}</span>
    </span>
  );

  const renderRow = (row: RowModel) => {
    const isSelected = selected.has(row.id);
    if (selectMode) {
      return (
        <li key={row.id}>
          <button
            type="button"
            role="checkbox"
            aria-checked={isSelected}
            onClick={() => toggleSelect(row.id)}
            className={`w-full flex items-start gap-3 text-left px-4 py-3 transition-colors duration-150 focus-visible:outline-offset-[-3px] ${
              isSelected ? "bg-accent-soft hover:bg-hover" : "hover:bg-subtle active:bg-raised"
            }`}
          >
            <span className="flex-shrink-0 mt-0.5 text-accent" aria-hidden="true">
              {isSelected ? <CheckSquare size={16} className="motion-check" /> : <Square size={16} className="text-muted" />}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium text-content leading-snug line-clamp-2">{row.title}</span>
              {rowMeta(row)}
            </span>
          </button>
        </li>
      );
    }

    if (confirmingId === row.id) {
      const busy = deletingId === row.id;
      return (
        <li key={row.id} className="motion-fade px-4 py-3 bg-danger-soft/60">
          <p className="text-sm text-content line-clamp-1">{row.title}</p>
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-xs text-danger">Delete this {noun} permanently?</span>
            <span className="flex items-center gap-1.5">
              <button
                type="button"
                autoFocus
                onClick={() => setConfirmingId(null)}
                disabled={busy}
                className="px-2.5 py-1.5 rounded-md text-xs font-medium text-content hover:bg-surface active:bg-raised disabled:opacity-50 transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(row.id)}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-danger-solid text-on-solid hover:bg-danger-hover active:bg-danger-hover disabled:opacity-60 disabled:cursor-wait transition-colors duration-150"
              >
                {busy && <Loader2 size={12} className="animate-spin" aria-hidden="true" />}
                {busy ? "Deleting…" : "Delete"}
              </button>
            </span>
          </div>
        </li>
      );
    }

    return (
      <li key={row.id} className="group flex items-center gap-3 pr-3">
        <button
          type="button"
          onClick={row.onOpen}
          className="flex-1 min-w-0 flex items-center gap-2 text-left pl-4 py-3 hover:bg-subtle active:bg-raised disabled:opacity-50 transition-colors duration-150 focus-visible:outline-offset-[-3px]"
        >
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium text-content leading-snug line-clamp-2">{row.title}</span>
            {rowMeta(row)}
          </span>
          <ChevronRight size={14} className="text-muted flex-shrink-0" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => { setDeleteError(null); setConfirmingId(row.id); }}
          aria-label={`Delete ${noun}: ${row.title}`}
          title={`Delete ${noun}`}
          className="grid flex-shrink-0 place-items-center w-9 h-9 rounded-md text-muted hover:text-danger hover:bg-danger-soft active:bg-danger-soft disabled:opacity-50 transition-[opacity,color,background-color] duration-150 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 focus-visible:opacity-100 focus-visible:outline-offset-2"
        >
          <Trash2 size={14} aria-hidden="true" />
        </button>
      </li>
    );
  };

  const tabButton = (value: Tab, label: string, Icon: typeof MessageCircle, count: number) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === value}
      onClick={() => switchTab(value)}
      className={`relative flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium transition-colors duration-150 ${
        tab === value ? "text-accent" : "text-muted hover:text-content"
      }`}
    >
      <Icon size={14} aria-hidden="true" />
      {label}
      {count > 0 && (
        <span className="ml-0.5 text-xs font-mono tabular-nums text-muted">{count}</span>
      )}
    </button>
  );

  return (
    <div aria-hidden={closing || undefined}>
      <div
        className={`fixed inset-0 z-40 bg-overlay/30 backdrop-blur-sm ${closing ? "history-exit" : "motion-fade"}`}
        onClick={closing ? undefined : onClose}
      />

      <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal={closing ? undefined : "true"} aria-label="Conversation history" className={`history-drawer fixed z-50 bg-surface shadow-2xl flex flex-col ${closing ? "history-exit" : "motion-drawer"}`}>
        <div className="flex items-center justify-between px-4 py-4 border-b border-line">
          <h2 className="font-semibold tracking-tight text-content text-base">History</h2>
          <div className="flex items-center gap-1">
            {!selectMode && currentIds.length > 0 && (
              <button
                type="button"
                onClick={() => { setConfirmingId(null); setSelectMode(true); }}
                className="px-2.5 py-1.5 rounded-md text-xs font-medium text-muted hover:text-content hover:bg-raised active:bg-pressed transition-colors duration-150"
              >
                Select
              </button>
            )}
            {selectMode && (
              <button
                type="button"
                onClick={exitSelectMode}
                className="px-2.5 py-1.5 rounded-md text-xs font-medium text-accent hover:bg-raised active:bg-pressed transition-colors duration-150"
              >
                Done
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close history"
              className="grid place-items-center w-9 h-9 rounded-md text-muted hover:text-content hover:bg-raised active:bg-pressed transition-colors duration-150"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="relative flex border-b border-line" role="tablist" aria-label="History type">
          <SlidingIndicator selector='[aria-selected="true"]' activeKey={tab} className="seg-indicator-underline" />
          {tabButton("chat", "Chat", MessageCircle, chatSessions.length)}
          {tabButton("debate", "Debates", Swords, debateSessions.length)}
        </div>

        {deleteError && (
          <div role="alert" className="motion-fade mx-4 mt-3 rounded-md border border-danger-line bg-danger-soft px-3 py-2 text-xs text-danger">
            {deleteError}
          </div>
        )}

        <div ref={listRef} tabIndex={-1} className="flex-1 overflow-y-auto focus:outline-none" role="tabpanel" aria-busy={loading}>
          {loading && currentIds.length === 0 ? (
            <ul aria-label="Loading history" className="divide-y divide-line">
              {[0, 1, 2, 3].map((i) => (
                <li key={i} className="px-4 py-3.5">
                  <div className="h-3.5 w-3/4 rounded bg-raised animate-pulse" />
                  <div className="mt-2 h-2.5 w-1/3 rounded bg-raised animate-pulse" />
                </li>
              ))}
            </ul>
          ) : loadError && currentIds.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-3 px-6 text-center text-sm text-muted" role="alert">
              <span>Couldn’t load your history.</span>
              <button type="button" onClick={refresh} className="button-secondary min-h-0 py-2 px-3 text-xs">
                Try again
              </button>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-muted text-sm gap-2 px-6 text-center">
              {tab === "chat" ? <MessageCircle size={24} className="text-muted" aria-hidden="true" /> : <Swords size={24} className="text-muted" aria-hidden="true" />}
              <span>{tab === "chat" ? "Chats you have while signed in appear here." : "Debates you run while signed in appear here."}</span>
            </div>
          ) : (
            <ul className="divide-y divide-line">{rows.map(renderRow)}</ul>
          )}
        </div>

        {selectMode ? (
          <div className="px-4 py-3 border-t border-line flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={toggleSelectAll}
              className="flex items-center gap-1.5 px-2 py-1.5 -ml-2 rounded-md text-xs font-medium text-content hover:bg-raised active:bg-pressed transition-colors duration-150"
            >
              {allSelected || someSelected ? <MinusSquare size={14} aria-hidden="true" /> : <Square size={14} aria-hidden="true" />}
              {allSelected ? "Deselect all" : "Select all"}
            </button>
            {confirmBulk ? (
              <span className="motion-fade flex items-center gap-1.5">
                <button
                  type="button"
                  autoFocus
                  onClick={() => setConfirmBulk(false)}
                  disabled={bulkDeleting}
                  className="px-2.5 py-1.5 rounded-md text-xs font-medium text-content hover:bg-raised active:bg-pressed disabled:opacity-50 transition-colors duration-150"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-danger-solid text-on-solid hover:bg-danger-hover active:bg-danger-hover disabled:opacity-60 disabled:cursor-wait transition-colors duration-150"
                >
                  {bulkDeleting && <Loader2 size={12} className="animate-spin" aria-hidden="true" />}
                  {bulkDeleting ? "Deleting…" : <>Delete <span className="tabular-nums">{selected.size}</span> permanently</>}
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmBulk(true)}
                disabled={selected.size === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-danger-solid text-on-solid hover:bg-danger-hover active:bg-danger-hover disabled:bg-raised disabled:text-muted disabled:cursor-not-allowed transition-colors duration-150"
              >
                <Trash2 size={12} aria-hidden="true" />
                Delete{selected.size > 0 && <span className="tabular-nums"> ({selected.size})</span>}
              </button>
            )}
          </div>
        ) : (
          <div className="px-4 py-3 border-t border-line text-xs text-muted text-center">
            History is private to your account
          </div>
        )}
      </div>
    </div>
  );
};

export default HistorySidebar;
